import { useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import {
  useTaskTodayControls,
  useTodaysCompletedHabits,
  useTodaysCompletedTasks,
} from 'features/tasks';
import uniqBy from 'lodash/uniqBy';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FullCalendarDay } from 'shared/components/ui/full-calendar-day';
import { useOpenTaskDetail } from 'shared/contexts/task-detail';
import { useToday } from 'shared/contexts/today';
import { useCalendarEventControls } from 'shared/hooks/use-calendar-event-controls';
import { useTodayScrollTime } from 'shared/hooks/use-today-scroll-time';
import { useTrackPageVisitEvent } from 'shared/hooks/use-track-page-visit-event';
import { useUser } from 'shared/hooks/use-user';
import { Paths } from 'shared/routes';
import { PageEvents } from 'shared/services/tracking';
import { ScheduleEntryState } from 'shared/types/habit-schedule';
import { ID } from 'shared/types/id';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { replaceUrlParams } from 'shared/utils/replace-url-params';
import { getScheduleByDate } from 'shared/utils/schedules';

import { useDayEvents } from '../../hooks/use-day-events';
import { useTodayTasks } from '../../hooks/use-today-tasks';
import { FilterButton } from './filter-button';
import * as Styled from './side-panel.style';

export const SidePanel: React.FC = () => {
  useTrackPageVisitEvent(PageEvents.TodayCalendar, undefined);
  const today = useToday();
  const user = useUser();
  const { t } = useTranslation();
  const calendarContainerRef = React.useRef<HTMLDivElement>(null);

  const todayTasks = useTodayTasks({
    includeFilters: true,
    includeCompleted: true,
  });
  const completedTasks = useTodaysCompletedTasks();
  const completedHabits = useTodaysCompletedHabits();
  const allTasks = useMemo(() => {
    const combinedTasks = uniqBy(
      [...todayTasks, ...completedTasks, ...completedHabits],
      'id',
    );
    return user?.settings?.todayHideHabits
      ? combinedTasks.filter(({ type }) => type !== TaskType.Habit)
      : combinedTasks;
  }, [
    completedHabits,
    completedTasks,
    todayTasks,
    user?.settings?.todayHideHabits,
  ]);

  const { events } = useDayEvents(today, allTasks);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();

  const navigate = useNavigate();
  const openTaskDetail = useOpenTaskDetail();
  const { onComplete } = useTaskTodayControls();
  const { onEventChange, onDrop, onSelect, onDateTime } =
    useCalendarEventControls(lifeAreas, goals, allTasks);

  const [referenceDate, setReferenceDate] = useState(today);
  const onToday = useCallback(() => setReferenceDate(new Date()), []);
  const { scrollTime } = useTodayScrollTime(true, onToday);

  const _openEventDetail = useCallback(
    (id: ID) => {
      const task = allTasks.find(({ id: taskId }) => taskId === id);
      if (task) {
        openTaskDetail(task);
        return;
      }

      const goal = goals.find(({ id: goalId }) => goalId === id);
      if (goal) {
        navigate(replaceUrlParams(Paths.GoalDetail, { id: goal.id }));
        return;
      }

      const lifeArea = lifeAreas.find(
        ({ id: lifeAreaId }) => lifeAreaId === id,
      );
      if (lifeArea) {
        navigate(replaceUrlParams(Paths.LifeAreaDetail, { id: lifeArea.id }));
        return;
      }
    },
    [allTasks, goals, lifeAreas, navigate, openTaskDetail],
  );

  const _onComplete = useCallback(
    (id: ID, date: Timestamp) => {
      const task = allTasks.find(({ id: taskId }) => taskId === id);
      if (!task) {
        return;
      }
      const daySchedule = getScheduleByDate(task.schedules, date);

      onComplete(task, ScheduleEntryState.Complete, {
        referenceDate: date,
        toggleDayEntry: !!daySchedule?.frequency,
      });
    },
    [allTasks, onComplete],
  );

  return (
    <Styled.Container>
      <Styled.Header>
        <Styled.Title>{t('pages.today.title')}</Styled.Title>

        <FilterButton />
      </Styled.Header>

      <Styled.Content>
        <FullCalendarDay
          ref={calendarContainerRef}
          tasks={allTasks}
          goals={goals}
          lifeAreas={lifeAreas}
          onCompleteTask={_onComplete}
          onClickEvent={_openEventDetail}
          events={events}
          eventChange={onEventChange}
          drop={onDrop}
          dateClick={onDateTime}
          select={onSelect}
          referenceDate={referenceDate}
          scrollTime={scrollTime}
          timeFormat={getTimeFormat(user)}
          selectable
          editable
          droppable
        />
      </Styled.Content>
    </Styled.Container>
  );
};
